'use client';

import { useEffect } from 'react';
import { DialogueProvider } from '@geberit/gdds';
import { useDispatch } from 'react-redux';

// types
import type { Scene } from 'components/Page/scenes';

// components
import { IsoLangProvider } from 'scenes/MetaData';
import SplashScreen from 'components/SplashScreen/SplashScreen';
import { CookieBannerController } from './cookie-banner-controller';
import MaintenancePage from 'scenes/ShoppingBasket/Maintenance/Maintenance';

// utils
import { PageWrapper } from './page-wrapper';
import { ContentContextProvider } from 'components/ContentArea/ContentContext';
import { EditingProvider } from 'components/ContentCreator/EditingContext';
import { PrevLocationContextProvider } from 'components/Page/prev-location-context';
import { CookieConsentProvider } from 'features/cookie-consent';
import { PageContextProvider } from 'components/Page/page-context';
import { ParentProvider } from 'scenes/parent-provider';
import { useWebshopMaintenance } from 'utils/hooks/use-webshop-maintenance';
import { fetchMaintenancePage } from 'components/Maintenance/actions';

export function Providers({
  children,
  pageType,
  metaLanguage,
  content,
}: Readonly<
  React.PropsWithChildren<{
    pageType: Scene;
    metaLanguage: string;
    content: Content;
  }>
>) {
  const dispatch = useDispatch<any>();

  useEffect(() => {
    dispatch(fetchMaintenancePage(metaLanguage));
  }, [dispatch, metaLanguage]);
  const webshopMaintenance = useWebshopMaintenance();

  // when maintenance is enabled show this page instead of webshop landingpage
  if (webshopMaintenance) {
    return (
      <ContentContextProvider>
        <MaintenancePage />
      </ContentContextProvider>
    );
  }

  return (
    <ParentProvider value={{ parent: 'page', pageType }}>
      <IsoLangProvider lang={metaLanguage}>
        <SplashScreen />
        <CookieConsentProvider>
          <EditingProvider>
            <ContentContextProvider>
              <DialogueProvider>
                <PrevLocationContextProvider>
                  <PageContextProvider content={content}>
                    <PageWrapper content={content} language={metaLanguage} pageType={pageType}>
                      <CookieBannerController />
                      {children}
                    </PageWrapper>
                  </PageContextProvider>
                </PrevLocationContextProvider>
              </DialogueProvider>
            </ContentContextProvider>
          </EditingProvider>
        </CookieConsentProvider>
      </IsoLangProvider>
    </ParentProvider>
  );
}
